import type { FC } from 'react';
import { motion, type Variants } from 'framer-motion';

import type {
  SanitySombraButton,
  SanitySombraStack,
} from '#/lib/sanity/queries/sombraStack';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import type { PageLayoutSharedProps } from '#/lib/types';
import { Button } from '#/components/Button';

import { SombraStackAnimation } from './SombraStackAnimation';

const REVEAL_VIEWPORT = { once: true, margin: '-100px' } as const;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.33, 1, 0.68, 1] },
  },
};

const REVEAL_PROPS = {
  variants: fadeUp,
  initial: 'hidden',
  whileInView: 'show',
  viewport: REVEAL_VIEWPORT,
} as const;

function SombraButton({ button }: { button: SanitySombraButton }) {
  if (!button.href) return null;

  return (
    <Button
      href={button.href}
      target={button.target}
      variant={button.variant}
      theme="dark"
      className="w-full text-center m:w-auto"
    >
      {button.title}
    </Button>
  );
}

export const SombraStack: FC<SanitySombraStack & PageLayoutSharedProps> = (
  props,
) => {
  return (
    <div
      data-sombra-section
      className="p-main flex flex-col items-center gap-24 bg-charcoal py-40 text-center text-cloud m:gap-56 m:py-100"
    >
      {/* Text + graphic + buttons pin together when the whole section doesn't
          fit the viewport (see SombraStackAnimation) */}
      <div
        data-sombra-pin-group
        className="flex w-full flex-col items-center gap-24 m:gap-56"
      >
        <div className="flex w-full max-w-800 flex-col items-center gap-48">
          {props.eyebrow ? (
            <motion.p
              {...REVEAL_PROPS}
              className="type-eyebrow text-electric"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'eyebrow',
              )}
            >
              {props.eyebrow}
            </motion.p>
          ) : null}

          <motion.h2
            {...REVEAL_PROPS}
            className="type-heading5 m:type-heading3 text-balance"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'title',
            )}
          >
            {props.title}
          </motion.h2>
        </div>

        <SombraStackAnimation notes={props.notes} />

        {props.buttons?.length ? (
          // Revealed by the GSAP timeline once the stack animation completes
          <div
            className="js-sombra-buttons flex w-full flex-col items-stretch gap-32 m:w-auto m:flex-row m:items-center"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'buttons',
            )}
          >
            {props.buttons.map((button) => (
              <SombraButton key={button._key} button={button} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};
